import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Modal from './Modal';
import { planLabel } from '../lib/plans';
import '../styles/adminMembers.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/users`;
export const memberRole = role => ({ venue: 'Mekân', organizer: 'Organizatör', admin: 'Yönetici', pool_manager: 'Personel' }[role] || '—');
export const memberDate = value => value && !Number.isNaN(new Date(value).getTime())
  ? new Date(value).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' }) : 'Kayıt yok';
const errorText = err => typeof err.response?.data?.detail === 'string'
  ? err.response.data.detail : 'Hesap bilgileri alınamadı. Yeniden deneyin.';

function useMember(userId) {
  const [member, setMember] = useState(null);
  const [error, setError] = useState('');
  const [revision, setRevision] = useState(0);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let current = true;
    setMember(null); setError(''); setLoading(!!userId);
    if (!userId) return undefined;
    axios.get(`${API}/${encodeURIComponent(userId)}`, { timeout: 12000 })
      .then(({ data }) => { if (current) setMember(data); })
      .catch(err => { if (current) setError(errorText(err)); })
      .finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [userId, revision]);
  return { member, error, loading, retry: () => setRevision(r => r + 1) };
}

export default function AdminMemberDetails({ userId, onDeleted }) {
  const { member, error, loading, retry } = useMember(userId);
  const [open, setOpen] = useState(false);
  useEffect(() => { setOpen(false); }, [userId]);

  if (!userId) return <p className="admin-member-panel">Bu profilin bağlı üyelik hesabı yok.</p>;
  if (loading) return <p className="admin-member-panel" role="status">Üyelik bilgileri yükleniyor…</p>;
  if (error) return <div className="admin-member-panel" role="alert"><p>{error}</p>
    <button type="button" className="admin-member-button" onClick={retry}>Tekrar dene</button></div>;
  if (!member) return null;
  const methods = (member.login_methods || []).map(m => ({ email: 'E-posta ve şifre', google: 'Google' }[m] || m)).join(' · ') || 'Henüz giriş yöntemi oluşturulmamış';
  return <section className="admin-member-panel" aria-label="Üyelik hesabı" data-testid={`admin-member-${userId}`}>
    <h3 className="font-semibold mb-4">Üyelik hesabı</h3>
    <dl className="admin-member-fields">
      <div><dt>Hesap sahibi</dt><dd>{member.name || '—'}</dd></div>
      <div><dt>Kayıtlı e-posta</dt><dd data-testid="member-email">{member.email || '—'}</dd></div>
      <div><dt>Hesap türü</dt><dd>{memberRole(member.role)}</dd></div>
      <div><dt>Kayıt tarihi</dt><dd>{memberDate(member.created_at)}</dd></div>
      <div><dt>Giriş yöntemleri</dt><dd>{methods}</dd></div>
      <div><dt>Üyelik planı</dt><dd>{planLabel(member.plan)}{member.plan_expires_at ? ` · Bitiş: ${memberDate(member.plan_expires_at)}` : ''}</dd></div>
      <div><dt>Hesap durumu</dt><dd>{member.deletion_pending ? 'Silme işlemi tamamlanmayı bekliyor' : member.approved ? 'Onaylı' : 'Onay bekliyor'}</dd></div>
      <div><dt>Bağlı profiller</dt><dd>{(member.profiles || []).map(p => p.name || memberRole(p.type === 'venues' ? 'venue' : 'organizer')).join(' · ') || 'Henüz profil oluşturulmamış'}</dd></div>
    </dl>
    {member.can_delete && <button type="button" className="admin-member-button admin-member-danger" data-testid="member-delete-open"
      onClick={() => setOpen(true)}>Üyeliği sil</button>}
    {open && <AdminMemberDeleteDialog key={userId} userId={userId} onClose={() => setOpen(false)} onDeleted={onDeleted} />}
  </section>;
}

// Both directory shortcuts and account details use this confirmation flow.
// Fetch on opening so the account identity, protections and affected counts are current.
export function AdminMemberDeleteDialog({ userId, onClose, onDeleted }) {
  const { member, error, loading, retry } = useMember(userId);
  const [email, setEmail] = useState('');
  const [busy, setBusy] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deleting = useRef(false);
  const counts = member?.deletion_counts || {};
  const canConfirm = email.trim().toLowerCase() === member?.email?.toLowerCase() && !!email.trim();
  const close = () => { if (!deleting.current) onClose(); };
  const remove = async event => {
    event.preventDefault();
    if (deleting.current || !canConfirm || !member?.can_delete) return;
    deleting.current = true; setBusy(true); setDeleteError('');
    try {
      await axios.delete(`${API}/${encodeURIComponent(userId)}`, {
        data: { confirmation_email: email.trim(), confirmed: true }, timeout: 45000,
      });
      toast.success('Üyelik hesabı silindi.');
      onClose();
      onDeleted?.(userId);
    } catch (err) {
      setDeleteError(err.response ? errorText(err) : 'İşlemin sonucu alınamadı. Aynı onayı yeniden göndererek kontrol edebilirsiniz.');
    } finally { deleting.current = false; setBusy(false); }
  };
  return <Modal open onClose={close} title="Üyelik hesabını sil" testId="member-delete-modal">
    {loading ? <p role="status">Üyelik bilgileri yükleniyor…</p>
      : error ? <div role="alert"><p>{error}</p><button type="button" className="admin-member-button" onClick={retry}>Tekrar dene</button></div>
      : !member?.can_delete ? <p role="alert">Bu hesap silinemez. Yönetici hesapları ve bağlı üyeliği olmayan profiller korunur.</p>
      : <form onSubmit={remove} className="admin-member-delete-form" data-testid="member-delete-form">
        <p><strong>{member.name}</strong><br /><span className="admin-member-email">{member.email}</span></p>
        <p>Bu hesap ve bağlı profilleri kalıcı olarak silinir. Açık oturumları geçersiz olur.</p>
        <ul className="list-disc pl-5">
          <li>{(counts.venues || 0) + (counts.organizers || 0)} profil, {counts.venue_interests || 0} başvuru / iş birliği ve {counts.matches || 0} eşleşme silinir.</li>
          <li>Hesaba ait fikirler, projeler, sohbetler, fotoğraflar ve kullanım kayıtları silinir. Bağlı başvurular karşı tarafın listesinden de kaldırılır.</li>
          <li>Sipariş ve ödeme kayıtları korunur ({member.retained_orders || 0} kayıt). Bu işlem ödeme iadesi yapmaz.</li>
        </ul>
        <label className="admin-member-confirm-label">Onaylamak için bu üyeye ait e-posta adresini yazın:
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={busy} autoComplete="off"
            spellCheck={false} required data-testid="member-delete-email" className="admin-member-input" />
        </label>
        {deleteError && <p role="alert" className="admin-member-error">{deleteError}</p>}
        {busy && <p role="status">Üyelik siliniyor… İşlem tamamlanana kadar bekleyin.</p>}
        <div className="admin-member-actions">
          <button type="submit" disabled={busy || !canConfirm} className="admin-member-button admin-member-danger" data-testid="member-delete-confirm">{busy ? 'Siliniyor…' : 'Üyeliği kalıcı olarak sil'}</button>
          <button type="button" onClick={close} disabled={busy} className="admin-member-button">Vazgeç</button>
        </div>
      </form>}
  </Modal>;
}
